import React, { useEffect, useRef, useState } from "react"
import { classNames } from "primereact/utils"
import { getAppMenuByRole } from "../../utils/menu.utils"
import { AppSubmenu } from "./AppSubmenu"
import { useSession } from "../../hooks/auth/useSession.hook"
import { BREAKPOINTS } from "../../consts/responsiveBreakpoints.consts"
import useSidebarContext from "../../hooks/contextConsumers/useSidebarContext.hook"
import useScreenDimensionsContext from "../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import AppMenuUserPreview from "./AppMenuUserPreview"
import AikaLogoDark from "../../design/assests/images/aika-logo-dark.svg"
import ReleaseVersion from "../../components/molecules/ReleaseVersion.molecule"
import ModalForm from "../../components/compounds/modalForm/ModalForm.compunds"
import { useNavigate } from "react-router-dom"
import { APP_MENU, APP_MENU_OVERLAYS } from "../../consts/appMenu.consts"
import { ShortcutOverlay } from "../../components/molecules/ShortcutOverlay.molecule"
import { buildAllowedShortcuts } from "../../utils/shortcuts.utils"

// TODO: temporal — acceso al chat de estimaciones para un usuario concreto
const ESTIMATION_AGENT_ACCESS_USER_ID = 28

function withEstimationsMenuForUser(menu, user) {
  if (user.id !== ESTIMATION_AGENT_ACCESS_USER_ID) {
    return menu
  }

  if (menu.some((item) => item.name === "Estimaciones")) {
    return menu
  }

  const estimationsItem = APP_MENU.find((item) => item.name === "Estimaciones")
  if (!estimationsItem) {
    return menu
  }

  return [
    ...menu,
    {
      name: estimationsItem.name,
      icon: estimationsItem.icon,
      modules: estimationsItem.modules,
    },
  ]
}

export default function AppMenu(props) {
  const { sidebarActive, setSidebarActive } = useSidebarContext()
  const [confirmLogoutVisible, setConfirmLogoutVisible] = useState(false)
  const navigate = useNavigate()
  const handleConfirmLogout = async () => {
    setSidebarActive(false)
    navigate("/logout")
  }
  const [availableShortcuts, setAvailableShortcuts] = useState([])

  const OPEN_CODE = 1
  const CLOSE_CODE = 2
  const [menuActive, setMenuActive] = useState(true)
  const { user } = useSession()
  const { screenWidth } = useScreenDimensionsContext()
  const model = withEstimationsMenuForUser(
    getAppMenuByRole(user.projectRole),
    user
  )

  const hasShortcuts = availableShortcuts.length > 0

  const menuModel =
    hasShortcuts ? model : (
      model.filter((item) => item.overlay !== APP_MENU_OVERLAYS.SHORTCUTS)
    )

  function onOverlayMenuItemClick(e, item) {
    if (item.overlay === APP_MENU_OVERLAYS.SHORTCUTS && hasShortcuts) {
      shortcutsOverlayRef.current?.toggle(e)
    }
  }

  async function fetchAvailableShortcuts() {
    const allowed = await buildAllowedShortcuts(user)
    const scorecard =
      user.projectRole === "admin" ?
        [
          {
            title: "Scorecard costo fijo",
            description: "Abrir scorecard",
            to: "/scorecard.html",
            visibility: {
              roles: ["admin"],
            },
            isLink: true,
          },
        ]
      : []
    setAvailableShortcuts([...allowed, ...scorecard])
  }

  const shortcutsOverlayRef = useRef(null)

  useEffect(() => {
    fetchAvailableShortcuts()
  }, [user])

  return (
    <>
      <div
        className={classNames("clickable-background", {
          "layout-sidebar-active":
            sidebarActive || screenWidth > BREAKPOINTS.TABLET_BREAKPOINT,
        })}
        onClick={() => setSidebarActive(false)}
      ></div>
      <div
        className={classNames("layout-menu-wrapper", {
          "layout-sidebar-active":
            sidebarActive || screenWidth > BREAKPOINTS.TABLET_BREAKPOINT,
        })}
      >
        <LogoTemplate />
        <div className="layout-menu-container flex flex-column justify-content-between">
          <div>
            <AppSubmenu
              items={menuModel}
              className="layout-menu"
              menuMode={props.menuMode}
              menuActive={menuActive}
              root
              parentMenuItemActive
              onMenuClick={null}
              onOverlayMenuItemClick={onOverlayMenuItemClick}
            />
          </div>
          <div className="hidden lg:block">
            <AppMenuUserPreview
              showLogoutModal={() => setConfirmLogoutVisible(true)}
            />
          </div>

          <ModalForm
            visible={confirmLogoutVisible}
            setVisible={setConfirmLogoutVisible}
            header={
              <div className="flex gap-3 align-items-center">
                <i
                  className="pi pi-exclamation-circle text-4xl"
                  style={{ color: "var(--clr-info__dark)" }}
                />
                <span className="text-3xl font-semibold font-cairo">
                  Cierre de sesión
                </span>
              </div>
            }
            saveLabel="Cerrar"
            closable={false}
            service={handleConfirmLogout}
          >
            ¿Estás seguro que deseas cerrar sesión?
          </ModalForm>
          <ShortcutOverlay
            ref={shortcutsOverlayRef}
            shortcuts={availableShortcuts}
          />
        </div>
      </div>
    </>
  )
}

const LogoTemplate = () => (
  <div className="sidebar-logo-container flex flex-column gap-2 flex justify-content-center align-items-center">
    <img
      src={AikaLogoDark}
      alt="Aika"
      height={42}
      width={80}
      className="block"
    />
    <ReleaseVersion className="text-center" />
  </div>
)
